import { Navigate, Outlet, createBrowserRouter } from "react-router-dom";

import AuthLayout from "@layouts/AuthLayout";
import MainLayout from "@layouts/MainLayout";
import ErrorLayout from "@layouts/ErrorLayout";
import Dashboard from "@pages/Dashboard";
import Login from "@pages/Login/Login";
import Logout from "@pages/Logout/Logout";
import { NotFoundPage, UnauthorizedPage } from "@pages/Error";
import ComponentAdd from "@pages/OpenSource/Component/ComponentAdd";
import ComponentDetail from "@pages/OpenSource/Component/ComponentDetail";
import ComponentList from "@pages/OpenSource/Component/ComponentList";
import ProjectDetail from "@pages/Projects/Project/ProjectDetail";
import ClarityVerification from "@pages/Projects/ClarityVerification/ClarityVerification";
import ClarityAIRVerification from "@pages/Projects/ClarityAIRVerification/ClarityAIRVerification";
import ProjectList from "@pages/Projects/Project/ProjectList";
import AddLicensePolicy from "@pages/Policy/LicensePolicy/AddLicensePolicy";
import EditLicensePolicy from "@pages/Policy/LicensePolicy/EditLicensePolicy";
import LicensePolicyDetail from "@pages/Policy/LicensePolicy/LicensePolicyDetail";
import LicensePolicyList from "@pages/Policy/LicensePolicy/LicensePolicyList";
import LicenseDetail from "@pages/OpenSource/License/LicenseDetail";
import LicenseList from "@pages/OpenSource/License/LicenseList";
import CveAnalysisList from "@pages/Status/CveAnalysis/CveAnalysisList";
import CveAnalysisDetail from "@pages/Status/CveAnalysis/CveAnalysisDetail";
import ComponentUsageStatus from "@pages/Status/ComponentUsageStatus/ComponentUsageStatus";
import FossIDVerification from "@pages/Projects/FossIDVerification/FossIDVerification";
import RequestDetail from "@pages/Projects/Request/RequestDetail";
import RequestList from "@pages/Projects/Request/RequestList";
import SbomVerification from "@pages/Projects/SbomVerification/SbomVerification";
import VerificationReportList from "@pages/Projects/VerificationReportList/VerificationReportList";
import CVEStatus from "@pages/Status/CVEStatus/CVEStatus";
import ComponentCVEStatus from "@pages/Status/CVEStatus/ComponentCVEStatus";
import LicenseUsageStatus from "@pages/Status/LicenseUsageStatus/LicenseUsageStatus";
import VulnerabilityStatus from "@pages/Status/VulnerabilityStatus/VulnerabilityStatus";
import ProtectedRoute, { ClientConfigProtectedRoute } from "@router/ProtectedRoute";
import NoticeInformationDetail from "@pages/Projects/NoticeInformation/NoticeInformationDetail";
import NoticeInformationList from "@pages/Projects/NoticeInformation/NoticeInformationList";
import AddSbomMerge from "@pages/Projects/SbomMerge/AddSbomMerge";
import SbomMergeDetail from "@pages/Projects/SbomMerge/SbomMergeDetail";
import SbomMergeList from "@pages/Projects/SbomMerge/SbomMergeList";

import MyProfile from "@pages/Profile/MyProfile";
import SbomList from "@pages/System/Sbom/SbomList";
import AddUser from "@pages/System/User/AddUser";
import UserDetail from "@pages/System/User/UserDetail";
import UserList from "@pages/System/User/UserList";
import Root from "./Root";
import PreferencesList from "@pages/System/Preferences/PreferencesList";
import ProductDetail from "@pages/Product/ProductDetail";
import Product from "@pages/Product/ProductList";
import AddProduct from "@pages/Product/AddProduct";
import FAQDetail from "@pages/Bulletin/FAQ/FAQDetail";
import FAQList from "@pages/Bulletin/FAQ/FAQList";
import paths, { routePermissions } from "./paths";

// Define base permissions for common routes
import AnnouncementAdd from "@pages/Bulletin/Announcement/AnnouncementAdd";
import AnnouncementDetail from "@pages/Bulletin/Announcement/AnnouncementDetail";
import AnnouncementList from "@pages/Bulletin/Announcement/AnnouncementList";
import ArchiveAdd from "@pages/Bulletin/Archive/ArchiveAdd";
import ArchiveDetail from "@pages/Bulletin/Archive/ArchiveDetail";
import ArchiveList from "@pages/Bulletin/Archive/ArchiveList";
import FAQAdd from "@pages/Bulletin/FAQ/FAQAdd";
import QAAdd from "@pages/Bulletin/Q&A/QAAdd";
import QADetail from "@pages/Bulletin/Q&A/QADetail";
import QAList from "@pages/Bulletin/Q&A/QAList";
import LicenseAdd from "@pages/OpenSource/License/LicenseAdd";
import { AssetClassificationList, VulnerabilityPolicyList } from "@pages/Policy";
import ImportedComponentDetail from "@pages/Projects/ImportedComponent/ImportedComponentDetail";
import ImportedComponentList from "@pages/Projects/ImportedComponent/ImportedComponentList";
import AddPreview from "@pages/Projects/Preview/AddPreview";
import PreviewDetail from "@pages/Projects/Preview/PreviewDetail";
import PreviewList from "@pages/Projects/Preview/PreviewList";
import VerificationComparison from "@pages/Projects/VerificationComparison/VerificationComparison";
import AddVerificationReport from "@pages/Projects/VerificationReportList/AddVerificationReport";
import VerificationReportDetail from "@pages/Projects/VerificationReportList/VerificationReportDetail";
import RoleList from "@pages/System/Role/RoleList";
import AddProject from "@pages/Projects/Project/AddProject";
import { BASE_PATH_RUNTIME } from "@configs/runtimeConfig"
import AddSbom from "@pages/System/Sbom/AddSbom";
import SbomDetail from "@pages/System/Sbom/SbomDetail";
import WorkHistoryList from "@pages/System/WorkHistory/WorkHistoryList";
import WorkHistoryDetail from "@pages/System/WorkHistory/WorkHistoryDetail";
import AddNoticeInformation from "@pages/Projects/NoticeInformation/AddNoticeInformation";
import ScanToolGuard from "@components/Guards/ScanToolGuard";
import { SCAN_TOOL_OPTIONS } from "@constants/scanTool";
import SbomReadableViewPage from "@pages/SbomReadableView/SbomReadableViewPage";

// Export basename
export const basename = (BASE_PATH_RUNTIME || "/").replace(/\/+$/, "");

const routes = [
  {
    element: <Root />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/login",
            element: <Login />,
          },
          {
            path: "/logout",
            element: <Logout />,
          },
        ],
      },
      {
        element: <MainLayout />,
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "/dashboard",
            element: <ProtectedRoute
              viewPermissions={routePermissions.dashboard.view}
            >
              <Dashboard />
            </ProtectedRoute>,
          },
          {
            path: "/projects",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.project.projectList.view}
                addPermissions={routePermissions.project.projectList.add}
                editPermissions={routePermissions.project.projectList.edit}
                deletePermissions={routePermissions.project.projectList.delete}
              >
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                path: "",
                element: <ProjectList />,
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute addPermissions={routePermissions.project.projectList.add}>
                    <AddProject />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":projectSeq",
                element: <ProjectDetail />,
              },
              {
                path: ":projectSeq/clarity",
                children: [
                  {
                    path: ":binaryVerificationSeq",
                    element: (
                      <ScanToolGuard requiredTool={SCAN_TOOL_OPTIONS.clarity.key}>
                        <ClarityVerification />
                      </ScanToolGuard>
                    ),
                  },
                ],
              },
              {
                path: ":projectSeq/fossid",
                children: [
                  {
                    path: ":sourceCodeVerificationSeq",
                    element: (
                      <ScanToolGuard requiredTool={SCAN_TOOL_OPTIONS.fossid.key}>
                        <FossIDVerification />
                      </ScanToolGuard>
                    ),
                  },
                ],
              },
              {
                path: ":projectSeq/clarity-air",
                children: [
                  {
                    path: ":clarityAirVerificationSeq",
                    element: (
                      <ScanToolGuard requiredTool={SCAN_TOOL_OPTIONS.clarityAIR.key}>
                        <ClarityAIRVerification />
                      </ScanToolGuard>
                    ),
                  },
                ],
              },
              {
                path: ":projectSeq/sbom",
                children: [
                  {
                    path: ":sbomVerificationSeq",
                    element: (
                      <ScanToolGuard requiredTool={SCAN_TOOL_OPTIONS.sbom.key}>
                        <SbomVerification />
                      </ScanToolGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "verification-report",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.verificationReportList.view}
                    editPermissions={routePermissions.project.verificationReportList.edit}
                    deletePermissions={routePermissions.project.verificationReportList.delete}
                  >
                    <VerificationReportList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.project.verificationReportList.edit}
                  >
                    <AddVerificationReport />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":verificationReportSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.verificationReportList.view}
                    editPermissions={routePermissions.project.verificationReportList.edit}
                  >
                    <VerificationReportDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "notice-information",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.project.noticeInformation.view}
                editPermissions={routePermissions.project.noticeInformation.edit}
                deletePermissions={routePermissions.project.noticeInformation.delete}
              >
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                path: "",
                element: (
                  <NoticeInformationList />
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.project.noticeInformation.edit}
                  >
                    <AddNoticeInformation />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":noticeInfoSeq",
                element: (
                  <NoticeInformationDetail />
                ),
              },
            ],
          },
          {
            path: "/verification-comparison",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.verificationComparison.view}
                    editPermissions={routePermissions.project.verificationComparison.edit}
                    deletePermissions={routePermissions.project.verificationComparison.delete}
                  >
                    <VerificationComparison />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/sbom-mergeds",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.sbomMerge.view}
                    editPermissions={routePermissions.project.sbomMerge.edit}
                    deletePermissions={routePermissions.project.sbomMerge.delete}
                  >
                    <SbomMergeList />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":mergedSbomSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.sbomMerge.view}
                    editPermissions={routePermissions.project.sbomMerge.edit}
                    deletePermissions={routePermissions.project.sbomMerge.delete}
                  >
                    <SbomMergeDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.project.sbomMerge.edit}
                    deletePermissions={routePermissions.project.sbomMerge.delete}
                  >
                    <AddSbomMerge />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            element: (
              <ClientConfigProtectedRoute
                clientConfigKey="usePreview"
                allowedValues={["Y", "yes", "true"]}
                redirectTo="/dashboard"
              />
            ),
            children: [
              {
                path: "/project-previews",
                children: [
                  {
                    path: "",
                    element: (
                      <ProtectedRoute
                        viewPermissions={routePermissions.project.previewsList.view}
                        editPermissions={routePermissions.project.previewsList.edit}
                        deletePermissions={routePermissions.project.previewsList.delete}
                      >
                        <PreviewList />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: "new",
                    element: (
                      <ProtectedRoute
                        editPermissions={routePermissions.project.previewsList.edit}
                      >
                        <AddPreview />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: ":projectPreviewSeq",
                    element: (
                      <ProtectedRoute
                        viewPermissions={routePermissions.project.previewsList.view}
                        editPermissions={routePermissions.project.previewsList.edit}
                      >
                        <PreviewDetail />
                      </ProtectedRoute>
                    )
                  },
                ],
              },
            ],
          },
          {
            path: "/requests",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.request.view}
                    editPermissions={routePermissions.project.request.edit}
                  >
                    <RequestList />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":actionSeq/:verificationTypeCd",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.project.request.view}
                    editPermissions={routePermissions.project.request.edit}
                  >
                    <ScanToolGuard>
                    <RequestDetail />
                    </ScanToolGuard>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            element: (
              <ClientConfigProtectedRoute
                clientConfigKey="useImportComponent"
                allowedValues={["Y", "yes", "true"]}
                redirectTo="/dashboard"
              />
            ),
            children: [
              {
                path: "imported-component-list",
                children: [
                  {
                    path: "",
                    element: (
                      <ProtectedRoute
                        viewPermissions={routePermissions.project.importedComponent.view}
                        editPermissions={routePermissions.project.importedComponent.edit}
                        deletePermissions={routePermissions.project.importedComponent.delete}
                      >
                        <ImportedComponentList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: ":importComponentSeq",
                    element: (
                      <ProtectedRoute
                        viewPermissions={routePermissions.project.importedComponent.view}
                        editPermissions={routePermissions.project.importedComponent.edit}
                      >
                        <ImportedComponentDetail />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
            ],
          },

          {
            path: "/components",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.openSource.componentList.view}
                    editPermissions={routePermissions.openSource.componentList.edit}
                    deletePermissions={routePermissions.openSource.componentList.delete}
                  >
                    <ComponentList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.openSource.componentList.edit}
                  >
                    <ComponentAdd />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":componentSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.openSource.componentList.view}
                    editPermissions={routePermissions.openSource.componentList.edit}
                  >
                    <ComponentDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/licenses",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.openSource.licenseList.view}
                    editPermissions={routePermissions.openSource.licenseList.edit}
                    deletePermissions={routePermissions.openSource.licenseList.delete}
                  >
                    <LicenseList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.openSource.licenseList.edit}
                  >
                    <LicenseAdd />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":licenseSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.openSource.licenseList.view}
                    editPermissions={routePermissions.openSource.licenseList.edit}
                  >
                    <LicenseDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/component-usage-status",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.status.componentUsageStatus.view}
                editPermissions={routePermissions.status.componentUsageStatus.edit}
              >
                <ComponentUsageStatus />
              </ProtectedRoute>
            ),
          },
          {
            path: "/vulnerability-status",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.status.vulnerabilityStatus.view}
                editPermissions={routePermissions.status.vulnerabilityStatus.edit}
              >
                <VulnerabilityStatus />
              </ProtectedRoute>
            ),
          },
          {
            path: "/license-usage-status",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.status.licenseUsageStatus.view}
                editPermissions={routePermissions.status.licenseUsageStatus.edit}
              >
                <LicenseUsageStatus />
              </ProtectedRoute>
            ),
          },
          {
            path: "/cve-status",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.status.cveStatus.view}
                editPermissions={routePermissions.status.cveStatus.edit}
              >
                <CVEStatus />
              </ProtectedRoute>
            ),
          },
          {
            path: "/cve-browsing",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.status.cveAnalysis.view}
                    editPermissions={routePermissions.status.cveAnalysis.edit}
                  >
                    <CveAnalysisList />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":cveAnalysisSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.status.cveAnalysis.view}
                    editPermissions={routePermissions.status.cveAnalysis.edit}
                  >
                    <CveAnalysisDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/component-cve-status/:componentCveId",
            element: (
              <ProtectedRoute
                viewPermissions={routePermissions.status.cveStatus.view}
                editPermissions={routePermissions.status.cveStatus.edit}
              >
                <ComponentCVEStatus />
              </ProtectedRoute>
            ),
          },
          {
            path: "/license-policy",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.policy.licensePolicy.view}
                    editPermissions={routePermissions.policy.licensePolicy.edit}
                    deletePermissions={routePermissions.policy.licensePolicy.delete}
                  >
                    <LicensePolicyList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.policy.licensePolicy.edit}
                  >
                    <AddLicensePolicy />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":licensePolicySeq",
                children: [
                  {
                    path: "",
                    element: (
                      <ProtectedRoute
                        viewPermissions={routePermissions.policy.licensePolicy.view}
                        editPermissions={routePermissions.policy.licensePolicy.edit}
                      >
                        <LicensePolicyDetail />
                      </ProtectedRoute>
                    )
                  },
                  {
                    path: "edit",
                    element: (
                      <ProtectedRoute
                        editPermissions={routePermissions.policy.licensePolicy.edit}
                      >
                        <EditLicensePolicy />
                      </ProtectedRoute>
                    )
                  },
                ],
              },
            ],
          },
          {
            path: "/vulnerability-policy",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.policy.vulnerabilityPolicy.view}
                    editPermissions={routePermissions.policy.vulnerabilityPolicy.edit}
                  >
                    <VulnerabilityPolicyList />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          
          {
            path: "/asset-classification",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.policy.assetClassification.view}
                    editPermissions={routePermissions.policy.assetClassification.edit}
                  >
                    <AssetClassificationList />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          
          {
            path: "/announcements",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.announcement.view}
                    editPermissions={routePermissions.bulletin.announcement.edit}
                    deletePermissions={routePermissions.bulletin.announcement.delete}
                  >
                    <AnnouncementList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.bulletin.announcement.edit}
                  >
                    <AnnouncementAdd />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":bulletinSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.announcement.view}
                    editPermissions={routePermissions.bulletin.announcement.edit}
                  >
                    <AnnouncementDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/faq",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.faq.view}
                    editPermissions={routePermissions.bulletin.faq.edit}
                    deletePermissions={routePermissions.bulletin.faq.delete}
                  >
                    <FAQList />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":faqSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.faq.view}
                    editPermissions={routePermissions.bulletin.faq.edit}
                  >
                    <FAQDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.bulletin.faq.edit}
                  >
                    <FAQAdd />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/question-and-answer",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.questionAndAnswer.view}
                    editPermissions={routePermissions.bulletin.questionAndAnswer.edit}
                    deletePermissions={routePermissions.bulletin.questionAndAnswer.delete}
                  >
                    <QAList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.bulletin.questionAndAnswer.edit}
                  >
                    <QAAdd />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":qnaSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.questionAndAnswer.view}
                    editPermissions={routePermissions.bulletin.questionAndAnswer.edit}
                  >
                    <QADetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/archive",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.archive.view}
                    editPermissions={routePermissions.bulletin.archive.edit}
                    deletePermissions={routePermissions.bulletin.archive.delete}
                  >
                    <ArchiveList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.bulletin.archive.edit}
                  >
                    <ArchiveAdd />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":archiveSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.bulletin.archive.view}
                    editPermissions={routePermissions.bulletin.archive.edit}
                  >
                    <ArchiveDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/profile",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.profile.view}
                  >
                    <MyProfile />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          
          {
            path: "/user",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.user.view}
                    editPermissions={routePermissions.system.user.edit}
                    deletePermissions={routePermissions.system.user.delete}
                  >
                    <UserList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.system.user.edit}
                  >
                    <AddUser />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":userId",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.user.view}
                    editPermissions={routePermissions.system.user.edit}
                  >
                    <UserDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/sbom",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.sbom.view}
                    editPermissions={routePermissions.system.sbom.edit}
                  >
                    <SbomList />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    editPermissions={routePermissions.system.sbom.edit}
                  >
                    <AddSbom />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":sbomSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.sbom.view}
                    editPermissions={routePermissions.system.sbom.edit}
                  >
                    <SbomDetail />
                  </ProtectedRoute>
                ),
              }
            ],
          },
          {
            path: "/products",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.product.view}
                    addPermissions={routePermissions.product.add}
                    editPermissions={routePermissions.product.edit}
                    deletePermissions={routePermissions.product.delete}
                  >
                    <Product />
                  </ProtectedRoute>
                ),
              },
              {
                path: "new",
                element: (
                  <ProtectedRoute
                    addPermissions={routePermissions.product.add}
                  >
                    <AddProduct />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":productSeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.product.view}
                    addPermissions={routePermissions.product.add}
                    editPermissions={routePermissions.product.edit}
                  >
                    <ProductDetail />
                  </ProtectedRoute>
                ),
              },
            ],
          },

          {
            path: "/role",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.role.view}
                    editPermissions={routePermissions.system.role.edit}
                  >
                    <RoleList />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/preferences",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.preferences.view}
                    editPermissions={routePermissions.system.preferences.edit}
                  >
                    <PreferencesList />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "/work-history",
            children: [
              {
                path: "",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.workHistory.view}
                  >
                    <WorkHistoryList />
                  </ProtectedRoute>
                ),
              },
              {
                path: ":historySeq",
                element: (
                  <ProtectedRoute
                    viewPermissions={routePermissions.system.workHistory.view}
                  >
                    <WorkHistoryDetail />
                  </ProtectedRoute>
                ),
              }
            ],
          },
        ],
      },
      {
        path: paths.sbomReadableView,
        element: (
          <ProtectedRoute viewPermissions={routePermissions.sbomReadableView.view}>
            <SbomReadableViewPage />
          </ProtectedRoute>
        ),
      },
      {
        element: <ErrorLayout />,
        children: [
          {
            path: "/unauthorized",
            element: <UnauthorizedPage />,
          },
          {
            path: "/404",
            element: <NotFoundPage />,
          },
          {
            path: "*",
            element: <Navigate to="/404" replace />,
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: basename,
});

export default routes;
